var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { Router } from '@angular/router';
import { LoopBackAuth, MemberApi, OrganisationsApi } from '@careapp/angular-sdk';
import { ADMIN_ROLE, DASHBOARD_ROLES } from '@careapp/core';
import { Actions, Effect } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { forkJoin as observableForkJoin, from as observableFrom, of as observableOf } from 'rxjs';
import 'rxjs/observable/forkJoin';
import 'rxjs/observable/from';
import { switchMap, withLatestFrom } from 'rxjs/operators';
import * as actions from '../actions';
import { FORCE_LOGOUT_401 } from '../actions';
import * as selectors from '../reducers';
export var NO_ORGANISATIONS = 'NO_ORGANISATIONS';
export var NO_USER = 'NO_USER';
export var NO_ROLES = 'NO_ROLES';
export var FAIL_TOKEN_REFRESH = 'FAIL_TOKEN_REFRESH';
var DAY_MS = 1000 * 60 * 60 * 24;
var StartupEffects = /** @class */ (function () {
    function StartupEffects(actions$, memberApi, store, orgsApi, loopbackAuth, router) {
        var _this = this;
        this.actions$ = actions$;
        this.memberApi = memberApi;
        this.store = store;
        this.orgsApi = orgsApi;
        this.loopbackAuth = loopbackAuth;
        this.router = router;
        this.applicationStartup = this.actions$
            .ofType(actions.RESPONSE_SUCCESS_AUTH_LOGIN, actions.RESPONSE_REHYDRATED)
            .pipe(withLatestFrom(this.store.select(selectors.auth.getAuth), this.store.select(selectors.getSelectedId('organisations')), this.store.select(selectors.auth.getRoles), this.store.select(selectors.auth.getLoggedInUser)), switchMap(function (_a) {
            var action = _a[0], auth = _a[1], selectedOrgId = _a[2], roles = _a[3], user = _a[4];
            if (!auth) {
                // We're at the login screen and don't have auth data yet - nothing to do
                return observableOf(new actions.ApplicationReady());
            }
            else {
                _this.loopbackAuth.setToken(auth);
            }
            return _this.refreshTokenIfRequired(auth).switchMap(function (updatedTokenAction) {
                return _this.performStartupFetches(auth.userId).pipe(switchMap(function (results) {
                    return _this.createObservableStreamFromResults(results, updatedTokenAction, selectedOrgId);
                }));
            });
        }));
    }
    StartupEffects.prototype.createObservableStreamFromResults = function (results, updatedTokenAction, selectedOrgId) {
        var failure = results.find(function (res) { return res.type === FORCE_LOGOUT_401; });
        var actionStream = results.concat([
            updatedTokenAction,
            new actions.ApplicationReady()
        ]);
        var memberResults = results[0], roleResults = results[1], orgResults = results[2];
        if (failure) {
            return observableFrom(actionStream);
        }
        if (!roleResults.payload.includes(ADMIN_ROLE)) {
            // Filter orgs based on user permissions (exclude non dashboard roles)
            var authorizedOrgs = orgResults.payload.filter(function (org) {
                var dashboardRoles = org.roles.filter(function (role) { return DASHBOARD_ROLES.includes(role); });
                return !!dashboardRoles.length;
            });
            if (!authorizedOrgs.length) {
                return observableFrom(actionStream.concat(new actions.ForceLogout401(NO_ORGANISATIONS)));
            }
        }
        var orgId = selectedOrgId || orgResults.payload[0].id;
        return observableFrom(actionStream.concat(new actions.ApplicationAuthorized({
            user: memberResults.payload,
            orgId: orgId,
            roles: roleResults.payload
        })));
    };
    StartupEffects.prototype.performStartupFetches = function (userId) {
        return observableForkJoin(this.memberApi
            .findById(userId)
            .map(function (updatedUser) { return new actions.ResponseSuccessLoggedInUser(updatedUser); })
            .catch(function (err) { return observableOf(new actions.ForceLogout401(NO_USER)); }), this.memberApi
            .listRoles(userId)
            .map(function (updatedRoles) { return new actions.ResponseSuccessUserRoles(updatedRoles); })
            .catch(function (err) { return observableOf(new actions.ForceLogout401(NO_ROLES)); }), this.orgsApi
            .find()
            .map(function (organisations) { return actions.Organisations.ResponseSuccessOwn(organisations); })
            .catch(function (err) { return observableOf(new actions.ForceLogout401(NO_ORGANISATIONS)); }));
    };
    StartupEffects.prototype.refreshTokenIfRequired = function (auth) {
        var _this = this;
        var msSinceCreated = DAY_MS;
        if (auth.created && auth.created.getTime) {
            msSinceCreated = Date.now() - auth.created.getTime();
        }
        else {
            return observableOf(new actions.ForceLogout401(FAIL_TOKEN_REFRESH));
        }
        if (msSinceCreated > DAY_MS || auth.dirty) {
            return this.memberApi
                .refreshToken(auth.userId, !auth.dirty)
                .map(function (updatedToken) {
                _this.loopbackAuth.setToken(updatedToken);
                return new actions.ResponseSuccessAuthRefreshAction(updatedToken);
            })
                .catch(function (ex) {
                if (ex.statusCode === 401) {
                    return observableFrom([
                        new actions.ResponseFailAuthRefreshAction(ex),
                        new actions.ForceLogout401(FAIL_TOKEN_REFRESH)
                    ]);
                }
                else {
                    // Failed to refresh for other reason - use stale token.
                    return observableOf(new actions.ResponseFailAuthRefreshAction(auth));
                }
            });
        }
        else {
            return observableOf(new actions.ResponseSuccessAuthRefreshAction(auth));
        }
    };
    __decorate([
        Effect(),
        __metadata("design:type", Object)
    ], StartupEffects.prototype, "applicationStartup", void 0);
    return StartupEffects;
}());
export { StartupEffects };
